import { PAGE_SX } from '../components/pageLayout';
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import { DataGrid } from "@mui/x-data-grid";

import {
  Search,
} from "@mui/icons-material";
import { format } from 'date-fns';

import Header from "../components/Header";
import FlexBetween from "../components/FlexBetween";
import CustomColumnMenu from "../components/DataGridCustomColumnMenu";

import useAxiosPrivate from "../hooks/useAxiosPrivate";
import { useAuthContext } from "../context/auth";
import { usePathContext } from "../context/path";
import { ROLES } from "../config/roles";
import Add from "../components/sifcodes/Add";
import Edit from "../components/sifcodes/Edit";
import Delete from "../components/sifcodes/Delete";
import { decodeParams } from "../utils/cryptoUtil";
import useGridStyles from '../hooks/useGridStyles';

const SIFCode = () => {
  const theme = useTheme();
  const { gridSx, gridBorder } = useGridStyles();

  const isNonMediumScreen = useMediaQuery("(min-width: 1200px)");

  const { auth } = useAuthContext();
  const { setTitle } = usePathContext();

  const axiosPrivate = useAxiosPrivate();

  const isRoot = auth?.roles?.includes(ROLES.Root);

  const root = auth && isRoot;

  const [sifCodes, setSifCodes] = useState([]);
  const [query, setQuery] = useState("");

  const [modeFilter, setModeFilter] = useState("");
  const [countryFilter, setCountryFilter] = useState("");
  const [enabledFilter, setEnabledFilter] = useState("");

  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);

  // --------------------------------------------------
  // Get SIF Codes
  // --------------------------------------------------
  useEffect(() => {
    const abortController = new AbortController();

    const getSIFCodes = async () => {
      try {
        setLoading(true);

        const response = await axiosPrivate.get("/api/sifCodes", {
          signal: abortController.signal,
        });
        const decodedResponse = decodeParams(response.data.result)
        setSifCodes(decodedResponse || []);
        setNotFound(false);
      } catch (err) {
        if (err.name === "CanceledError") {
          return;
        }

        console.error("Failed to get SIF codes:", err);

        setSifCodes([]);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    getSIFCodes();

    return () => {
      abortController.abort();
    };
  }, [auth, axiosPrivate]);

  // --------------------------------------------------
  // Page title
  // --------------------------------------------------
  useEffect(() => {
    setTitle("SIF Code Management");
  }, [setTitle]);

  // --------------------------------------------------
  // Country list
  // --------------------------------------------------
  const countries = useMemo(() => {
    return [...new Set(
      (sifCodes || [])
        .map((item) => item.countryISO)
        .filter(Boolean)
    )].sort();
  }, [sifCodes]);

  // --------------------------------------------------
  // Filter
  // --------------------------------------------------
  const filteredSIFCodes = useMemo(() => {
    const search = query.trim().toLowerCase();

    return [...(sifCodes || [])]
      .filter((item) => {
        if (!search) return true;

        return (
          item.code?.toLowerCase().includes(search) ||
          item.mode?.toLowerCase().includes(search) ||
          item.countryISO?.toLowerCase().includes(search) ||
          item.description?.toLowerCase().includes(search)
        );
      })
      .filter((item) => {
        if (!modeFilter) return true;

        return item.mode === modeFilter;
      })
      .filter((item) => {
        if (!countryFilter) return true;

        return item.countryISO === countryFilter;
      })
      .filter((item) => {
        if (enabledFilter === "") return true;

        return item.enabled === (enabledFilter === "true");
      });
  }, [
    sifCodes,
    query,
    modeFilter,
    countryFilter,
    enabledFilter,
  ]);

  // --------------------------------------------------
  // Columns
  // --------------------------------------------------
  const columns = [
    {
      field: "order",
      headerName: "No.",
      flex: 0.2,
      sortable: false,
      filterable: false,

      renderCell: (params) => {
        if (!params.api || !params.id) {
          return null;
        }

        const index = params.api
          .getAllRowIds?.()
          .indexOf(params.id);

        return <span>{index + 1}</span>;
      },
    },

    {
      field: "mode",
      headerName: "Mode",
      flex: 0.3,

      renderCell: (params) => {
        const modeNames = {
          M1: "Mode 1",
          M2: "Mode 2",
          M3A: "Mode 3/A",
        };

        return modeNames[params.value] || params.value;
      },
    },

    {
      field: "code",
      headerName: "SIF Code",
      flex: 0.4,

      renderCell: (params) => (
        <Typography
          fontWeight="bold"
          sx={{
            letterSpacing: "0.08rem",
          }}
        >
          {params.value}
        </Typography>
      ),
    },

    {
      field: "countryISO",
      headerName: "Country",
      flex: 0.5,

      renderCell: (params) => {
        const iso = params.value?.toLowerCase();

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              height: "100%",
            }}
          >
            {iso && (
              <span
                className={`fi fi-${iso}`}
                style={{
                  fontSize: "1.3rem",
                  display: "inline-block",
                }}
              />
            )}

            <Typography
              fontWeight="bold"
              sx={{
                color: theme.palette.secondary[100],
              }}
            >
              {params.value || "-"}
            </Typography>
          </Box>
        );
      },
    },

    {
      field: "description",
      headerName: "Description",
      flex: 1,
    },

    { 
      field: 'createdAt', 
      headerName: 'Created At', 
      flex: 0.4,
      renderCell: (params) => {
        return format(new Date(params.value), 'yyyy-MM-dd HH:mm:ss');
      }
    },
    { 
      field: 'updatedAt', 
      headerName: 'Updated At', 
      flex: 0.4,
      renderCell: (params) => {
        return format(new Date(params.value), 'yyyy-MM-dd HH:mm:ss');
      }
    },

    {
      field: "enabled",
      headerName: "Status",
      flex: 0.2,

      renderCell: (params) => (
        <>
          {root && (
            <Switch
              checked={Boolean(params.row.enabled)}
              onChange={() =>
                handleEnabledChange(params.row._id)
              }
              size="small"
              sx={{
                width: 42,
                height: 24,
                padding: 0,

                "& .MuiSwitch-switchBase": {
                  padding: "3px",

                  "&.Mui-checked": {
                    transform: "translateX(18px)",

                    color: theme.palette.getContrastText(
                      theme.palette.secondary.main
                    ),

                    "& + .MuiSwitch-track": {
                      backgroundColor: theme.palette.secondary.main,
                      opacity: 1,
                    },
                  },
                },

                "& .MuiSwitch-thumb": {
                  width: 18,
                  height: 18,
                  boxShadow: "0 1px 3px rgba(0,0,0,0.4)",
                },

                "& .MuiSwitch-track": {
                  borderRadius: 12,
                  backgroundColor: theme.palette.grey[600],
                  opacity: 1,
                },
              }}
            />
        )}
        </>
      ),
    },

    {
      field: "actions",
      headerName: "Action",
      type: "actions",
      width: 120,

      renderCell: (params) => (
        <>
          {root && (
            <Box>
              <Edit
                id={params.row._id}
                mode={params.row.mode}
                code={params.row.code}
                countryISO={params.row.countryISO}
                description={params.row.description}
                enabled={params.row.enabled}
                onUpdated={(updatedSIFCode) => {
                  setSifCodes((prev) =>
                    prev.map((item) =>
                      item._id === updatedSIFCode._id
                        ? updatedSIFCode
                        : item
                    )
                  );
                }}
              />

              <Delete
                id={params.row._id}
                code={params.row.code}
                countryISO={params.row.countryISO}
                mode={params.row.mode}
                onDeleted={(deletedId) => {
                  setSifCodes((prev) =>
                    prev.filter((item) => item._id !== deletedId)
                  );
                }}
              />
            </Box>
            )}
        </>
      ),
    },
  ];

  const handleEnabledChange = async (id) => {
    try {
      const response = await axiosPrivate.patch(
        `/api/sifcodes/${id}/toggle`
      );

      setSifCodes((prev) =>
        prev.map((item) =>
          item._id === id
            ? { ...item, enabled: response.data.enabled }
            : item
        )
      );
    } catch (error) {
      console.error("Failed to update SIF code status:", error);
    }
  };

  // --------------------------------------------------
  // Render
  // --------------------------------------------------
  return (
    <Box sx={PAGE_SX}>
      <FlexBetween
        sx={{
          flexDirection: {
            xs: "column",
            sm: "row",
          },

          alignItems: {
            xs: "flex-start",
            sm: "center",
          },

          gap: "1rem",
        }}
      >
        {/* Header */}
        <Header
          title="SIF CODES"
          subtitle="Manage Mode 1, Mode 2 and Mode 3/A identification codes"
        />

        {/* Filters */}
        <FlexBetween
          sx={{
            gap: "0.75rem",

            flexWrap: "wrap",

            justifyContent: {
              xs: "flex-start",
              sm: "flex-end",
            },
          }}
        >
          {/* Search */}
          <TextField
            label="Search"
            placeholder="Code, country, mode..."
            variant="standard"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            sx={{
              width: "14rem",
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Search
                    sx={{
                      color: theme.palette.secondary[200],
                    }}
                  />
                </InputAdornment>
              ),
            }}
          />

          {/* Mode */}
          <FormControl
            variant="standard"
            sx={{
              minWidth: "7rem",
            }}
          >
            <InputLabel>Mode</InputLabel>

            <Select
              value={modeFilter}
              onChange={(e) =>
                setModeFilter(e.target.value)
              }
            >
              <MenuItem value="">
                All
              </MenuItem>

              <MenuItem value="M1">
                Mode 1
              </MenuItem>

              <MenuItem value="M2">
                Mode 2
              </MenuItem>

              <MenuItem value="M3A">
                Mode 3/A
              </MenuItem>
            </Select>
          </FormControl>

          {/* Country */}
          <FormControl
            variant="standard"
            sx={{
              minWidth: "7rem",
            }}
          >
            <InputLabel>Country</InputLabel>

            <Select
              value={countryFilter}
              onChange={(e) =>
                setCountryFilter(e.target.value)
              }
            >
              <MenuItem value="">
                All
              </MenuItem>

              {countries.map((country) => (
                <MenuItem
                  key={country}
                  value={country}
                >
                  {country}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Status */}
          <FormControl
            variant="standard"
            sx={{
              minWidth: "7rem",
            }}
          >
            <InputLabel>Status</InputLabel>

            <Select
              value={enabledFilter}
              onChange={(e) =>
                setEnabledFilter(e.target.value)
              }
            >
              <MenuItem value="">
                All
              </MenuItem>

              <MenuItem value="true">
                Enabled
              </MenuItem>

              <MenuItem value="false">
                Disabled
              </MenuItem>
            </Select>
          </FormControl>

          {/* Add */}
          <Add
            onCreated={(newSIFCode) => {
              setSifCodes((prev) => [
                ...prev,
                newSIFCode,
              ]);
            }}
          />
        </FlexBetween>
      </FlexBetween>

      {/* =========================================
          DATA GRID
      ========================================= */}
      <Box
        mt="1rem"
        flex={1}
        minHeight="400px"
        backgroundColor={theme.palette.background.alt}
        border={gridBorder}
        borderRadius="0.55rem"
        overflow="hidden"
      >
        <DataGrid
          loading={loading}
          getRowId={(row) => row._id}
          rows={filteredSIFCodes}
          columns={columns}
          disableRowSelectionOnClick
          components={{
            ColumnMenu: CustomColumnMenu,
          }}
          sx={gridSx}
        />
      </Box>

      {/* No results */}
      {!loading &&
        !filteredSIFCodes.length &&
        (query ||
          modeFilter ||
          countryFilter ||
          enabledFilter !== "") && (
          <Typography
            sx={{
              mt: 2,
              color: theme.palette.secondary[200],
            }}
          >
            No matching SIF codes found.
          </Typography>
        )}

      {/* No records */}
      {!loading &&
        notFound &&
        !sifCodes?.length && (
          <Typography
            sx={{
              mt: 2,
              color: theme.palette.secondary[200],
            }}
          >
            No SIF code records found.
          </Typography>
        )}
    </Box>
  );
};

export default SIFCode;